<template>
  <ul
    class="breadcrumbs-list"
    :class="{
      'breadcrumbs-list--collapse-middle-items': !skipCollapseMiddleItems && collapseMiddleItems,
      'breadcrumbs-list--skip-collapse-middle-items': skipCollapseMiddleItems,
      'breadcrumbs-list--truncate-first-item': titledBreadcrumbs.length <= 2 && !truncateLastItem,
      'breadcrumbs-list--truncate-last-item': truncateLastItem,
    }"
  >
    <li class="breadcrumbs-list__item">
      <tm-button
        icon-only
        class="breadcrumbs-list__go-to-back-button"
        size="medium"
        @click="goToBack"
      >
        <tm-icon-chevron-left-svg />
      </tm-button>
    </li>
    <li
      v-for="(breadcrumb, index) in filteredBreadcrumbs"
      :key="`${breadcrumb.label} ${maxLeftVisible} ${maxRightVisible}`"
      class="breadcrumbs-list__item"
    >
      <tm-dropdown
        v-if="breadcrumb.hiddenBreadcrumbs?.length"
        class="d-flex"
      >
        <tm-button
          size="small"
          flat
          no-border
          class="breadcrumbs-list__hidden-trigger"
        >
          {{ breadcrumb.label }}
        </tm-button>
        <template v-slot:menu>
          <tm-dropdown-item
            v-for="hiddenBreadcrumb in breadcrumb.hiddenBreadcrumbs"
            :key="hiddenBreadcrumb.label"
            :label="hiddenBreadcrumb.label"
            :to="hiddenBreadcrumb.route"
            @click="hiddenBreadcrumb.onClick?.()"
          />
        </template>
      </tm-dropdown>
      <tm-conditional-link
        v-else
        :to="breadcrumb.route"
        :tag="breadcrumb.onClick ? 'a' : 'span'"
        @click="breadcrumb.onClick?.()"
      >
        <slot
          name="label"
          :index="index"
          :label="breadcrumb.label"
        >
          {{ breadcrumb.label }}
        </slot>
      </tm-conditional-link>

      <tm-icon
        v-if="index + 1 < filteredBreadcrumbs.length"
        name="tmi-chevron-right"
        size="xSmall"
        class="breadcrumbs-list__divider distinct--color pl-1"
      />
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed } from '@/composition/vue/compositionApi'
import type { Breadcrumb } from '@/components/shared/types'
import TmButton from '@/components/shared/TmButton.vue'
import TmIcon from '@/components/shared/TmIcon.vue'
import TmConditionalLink from '@/components/shared/TmConditionalLink.vue'
import TmDropdown from '@/components/shared/TmDropdown.vue'
import TmDropdownItem from '@/components/shared/TmDropdownItem.vue'
import TmIconChevronLeftSvg from '@/components/shared/svgIcons/TmIconChevronLeftSvg.vue'
import { useGoToBackAction } from '@/composition/router'

const { titledBreadcrumbs, collapseMiddleItems, truncateLastItem, maxLeftVisible, maxRightVisible } = defineProps<{
  titledBreadcrumbs: Breadcrumb[]
  collapseMiddleItems?: boolean // Replace middle items to '...'
  truncateLastItem?: boolean
  maxLeftVisible?: number
  maxRightVisible?: number
}>()

const skipCollapseMiddleItems = computed(() => titledBreadcrumbs.length <= 3)

type FilteredBreadcrumb = Breadcrumb & {
  hiddenBreadcrumbs?: Breadcrumb[]
}

const filteredBreadcrumbs = computed<FilteredBreadcrumb[]>(() => {
  if (maxLeftVisible !== undefined || maxRightVisible !== undefined) {
    const leftCount = maxLeftVisible ?? 0
    const rightCount = maxRightVisible ?? 0
    if (titledBreadcrumbs.length <= leftCount + rightCount + 1) {
      return titledBreadcrumbs
    }

    const left = titledBreadcrumbs.slice(0, leftCount)
    const hiddenBreadcrumbs = titledBreadcrumbs.slice(leftCount, titledBreadcrumbs.length - rightCount)
    const right = titledBreadcrumbs.slice(titledBreadcrumbs.length - rightCount)
    return [...left, { label: '...', hiddenBreadcrumbs }, ...right]
  }

  if (collapseMiddleItems && titledBreadcrumbs.length > 2) {
    return [titledBreadcrumbs[0], { label: '...' }, titledBreadcrumbs.at(-1)!] satisfies Breadcrumb[]
  }
  return titledBreadcrumbs
})

const goToBack = computed(
  () => titledBreadcrumbs.at(-2)?.onClick ?? useGoToBackAction(filteredBreadcrumbs.value.at(-2)?.route),
)
</script>

<style lang="scss" scoped>
.breadcrumbs-list {
  display: inline-flex;
  padding: 4px;
  margin: -4px;
  overflow: hidden;

  &--skip-collapse-middle-items {
    display: flex;
  }

  &--truncate-first-item {
    .breadcrumbs-list__item {
      &:nth-child(2) {
        flex-shrink: 10;
      }
    }
  }

  &--truncate-last-item {
    .breadcrumbs-list__item {
      &:last-child {
        flex-shrink: 1;
      }
    }
  }

  &__divider {
    margin: 0 6px;
  }

  &__hidden-trigger {
    padding: 0 4px;
    font-size: inherit;
  }

  &__go-to-back-button {
    margin-right: 12px;
    border-radius: 4px !important;
  }

  &__item {
    display: flex;
    align-items: center;
    flex-shrink: 10;
    margin: -4px;
    padding: 4px;
    font-size: 12px;
    overflow: hidden;
    list-style-type: none;

    &:nth-child(1),
    &:nth-child(2) {
      flex-shrink: 0;
    }

    &:last-child {
      flex-shrink: 0;
    }

    > a,
    > span {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
    a,
    span {
      line-height: 1.5;
      font-weight: $medium-font;
      color: $neutral;
    }
    a {
      cursor: pointer;

      &:hover {
        color: $emphasize;
      }
    }
    .tm-icon {
      color: $distinct;
    }
  }

  &.breadcrumbs-list--collapse-middle-items {
    .breadcrumbs-list__item {
      &:nth-child(2) {
        flex-shrink: 10;
      }

      /* Ellipsis */
      &:nth-child(3) {
        flex-shrink: 0;
      }
    }
  }
}
</style>
